import {
  useDynamicContext,
  useRefreshUser,
  useUserUpdateRequest,
  useUserWallets,
} from '@dynamic-labs/sdk-react-core';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { useState } from 'react';

import { registerSuccessAtom, selectedAddressAtom } from '@claim/atoms';

import { getSolanaBalance } from '../utils/getSolanaBalance';
import { useIsClaimActive } from './use-claim-active';

/**
 * Custom hook for linking a user's SOL wallet.
 *
 * This hook manages the selected SOL wallet, updates the user metadata,
 * and refreshes the user data if a claim is active.
 *
 * @param isClaimActive - Determines if the airdrop claim process is active.
 * @returns An object containing the selected wallet address, setter,
 *          whether the user has a SOL wallet, the metadata update function,
 *          and a flag indicating if the user is ready.
 */
export const useWalletLinking = () => {
  const { user } = useDynamicContext();
  const { updateUser } = useUserUpdateRequest();
  const userWallets = useUserWallets();
  const refresh = useRefreshUser();
  const isClaimActive = useIsClaimActive();

  const setRegisterSuccess = useSetAtom(registerSuccessAtom);

  // Manage the selected wallet address via atom.
  const [selectedAddress, setSelectedAddress] = useAtom(selectedAddressAtom);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [userReady, setUserReady] = useState(false);

  // Check if the user has any SOL wallet.
  const hasSolWallet = userWallets.some((wallet) => wallet.chain === 'SOL');
  const solWallets = userWallets.filter((wallet) => wallet.chain === 'SOL');

  /**
   * Updates the user's metadata with the selected SOL wallet address.
   * If the claim is not active, registration is marked as successful.
   * Otherwise, the user data is refreshed and the userReady flag is set.
   */
  const handleUpdateMetadata = async () => {
    setUpdatingMetadata(true);
    try {
      await updateUser({
        metadata: {
          ...user?.metadata,
          ...(!isClaimActive ? {} : { solanaClaimAddress: selectedAddress }),
          ...(isClaimActive
            ? {}
            : { solanaPreregistrationAddress: selectedAddress }),
        },
      });

      if (!isClaimActive) {
        setRegisterSuccess(true);
        return;
      }

      await refresh();
      setUserReady(true);
    } catch (error) {
      console.error('Error updating user metadata:', error);
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // Query to check the if the selectedAddress is still connected. If not, switch to the first SOL wallet.
  useQuery({
    queryKey: [
      'checking connected wallets',
      userWallets.map((wallet) => wallet.address),
    ],
    queryFn: () => {
      if (userWallets.some((wallet) => wallet.address !== selectedAddress)) {
        setSelectedAddress(solWallets[0]?.address ?? '');
      }
      return true;
    },
  });

  const { data: balance, isLoading: isLoadingBalance } = useQuery({
    queryKey: ['balance', selectedAddress],
    queryFn: async () => {
      return await getSolanaBalance(selectedAddress ?? '');
    },
    enabled: !!selectedAddress,
  });

  const hasEnoughSol = Number(balance) >= 0.01;

  return {
    selectedAddress,
    setSelectedAddress,
    hasSolWallet,
    handleUpdateMetadata,
    updatingMetadata,
    userReady,
    hasEnoughSol,
    isLoadingBalance,
  };
};
